import { createTheme } from "@mui/material/styles";
import { colors, fonts } from "../theme/tokens";

const primaryWhiteColor = colors.white;
const primaryBlueColor = colors.primaryBlue;
const PrimaryLightTextColor = colors.lightGray;
const PrimaryTextColor = colors.black;
const primaryRedColor = colors.red;
const borderColor = colors.primaryBorder;
const borderStyle = "1px solid " + borderColor;
const primaryLightGray = colors.grayText2;
const primaryLightGreen = "rgba(15, 255, 126, 0.25)";
const primaryGrayBg = colors.bgColor;
const defaultFontSize = 14;
const defaultBoxShadow = "0 0 0 0.2rem rgba(132, 28, 57, 0.15)";
const drawerWidthCollapsed = 80;
const drawerWidthExpanded = 260;
const drawerWidth = drawerWidthExpanded;
const subMenuWidth = 250;
const borderRadius = "4px";
const primarySteperColor = colors.primaryBlue2;
const primarygreenColor = colors.primaryGreen;

const mainContainer = {
  margin: "30px",
};

const lightFont = {
  fontFamily: fonts.regular,
  fontWeight: 400,
  fontStyle: "normal",
};

const mediumFont = {
  fontFamily: fonts.regular,
  fontWeight: 500,
};

const boldFont = {
  fontFamily: fonts.bold,
  fontWeight: 700,
};

const regularFont = {
  fontFamily: fonts.regular,
  fontWeight: 400,
};

const normalFont = {
  fontFamily: fonts.regular,
  fontWeight: 400,
};

const thinFont = {
  fontFamily: fonts.regular,
  fontWeight: 400,
};

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1920,
    },
  },
  palette: {
    primary: {
      main: colors.primaryBlue,
      dark: colors.primaryBlue2,
      light: colors.secondaryBlue,
      contrastText: colors.white,
    },
    secondary: {
      main: colors.primaryGreen,
      contrastText: colors.primaryBlue,
    },
    error: {
      main: colors.red,
    },
    text: {
      primary: colors.black,
      secondary: colors.secondaryText,
    },
    background: {
      default: colors.bgColor,
      paper: colors.white,
    },
    divider: colors.primaryBorder,
  },
  typography: {
    fontSize: 14,
    fontFamily: fonts.regular,
    button: {
      textTransform: "none",
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
        },
        containedPrimary: {
          backgroundColor: colors.primaryBlue,
          "&:hover": {
            backgroundColor: colors.primaryBlue2,
            color: colors.primaryGreen,
          },
        },
        outlinedPrimary: {
          borderColor: colors.primaryBlue,
          color: colors.primaryBlue,
          "&:hover": {
            backgroundColor: colors.primaryBlue2,
            borderColor: colors.primaryBlue2,
            color: colors.primaryGreen,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: colors.white,
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.primaryBlue,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.primaryBlue,
            borderWidth: 1,
          },
        },
        notchedOutline: {
          borderColor: colors.primaryBorder,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.primaryBlue,
          color: colors.white,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.white,
          color: colors.black,
          boxShadow: "0 1px 0 " + colors.primaryBorder,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.bgColor,
          color: colors.black,
        },
      },
    },
  },
});

const getRelativeFontSize = (value: number = 0) => {
  let size = defaultFontSize + value;
  return size + "px";
};

const heading = {
  fontSize: getRelativeFontSize(10),
  ...boldFont,
  lineHeight: "29px",
  color: colors.primaryBlue2,
};

const subHeading = {
  color: colors.secondaryText,
  fontSize: getRelativeFontSize(-2),
  ...regularFont,
  letterSpacing: "-0.01em",
};

const customButtonStyle = {
  borderRadius: borderRadius,
  border: "none",
  fontSize: getRelativeFontSize(),
  textAlign: "center" as const,
  backgroundColor: primaryWhiteColor,
  padding: "10px 15px",
  boxShadow: "none",
  color: "white",
  cursor: "pointer",
  textTransform: "none" as const,
};

const customTextFieldStyle = {
  borderRadius: "8px",
  position: "relative" as const,
  border: "none",
  fontSize: getRelativeFontSize(2),
  backgroundColor: colors.white,
  padding: "10px 15px",
  boxShadow: "none",
  width: "100%",
};

const headingText = {
  display: "inline-block",
  fontSize: getRelativeFontSize(8),
};

const centerItemFlex = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const centerItemAbsolute = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%,-50%)",
};

export {
  borderRadius,
  borderColor,
  borderStyle,
  defaultBoxShadow,
  customButtonStyle,
  customTextFieldStyle,
  headingText,
  centerItemFlex,
  centerItemAbsolute,
  regularFont,
  boldFont,
  mediumFont,
  lightFont,
  normalFont,
  thinFont,
  getRelativeFontSize,
  theme,
  mainContainer,
  primaryBlueColor,
  primaryWhiteColor,
  PrimaryTextColor,
  primaryRedColor,
  PrimaryLightTextColor,
  primaryLightGray,
  drawerWidth,
  drawerWidthCollapsed,
  drawerWidthExpanded,
  primaryLightGreen,
  primaryGrayBg,
  subMenuWidth,
  primarySteperColor,
  heading,
  subHeading,
  primarygreenColor,
};
